import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { LuArrowLeft, LuCode2, LuCopy, LuEye, LuPalette } from "react-icons/lu";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";


import { fetchRestaurantDetails } from "../actions/restaurantActions";
import Loader from "../components/Loader";
import Messages from "../components/Messages";
import { OverviewButton } from "../components/overview/overviewStyles";

const PREVIEW_SIZES = {
  small: {
    label: "Pequeno",
    width: 360,
    height: 450
  },
  medium: {
    label: "Médio",
    width: 500,
    height: 600
  },
  large: {
    label: "Grande",
    width: 720,
    height: 760
  }
};

export const Page = styled.main`
  width: 100%; min-width: 0; margin: 0; padding: 24px 28px;
  box-sizing: border-box; background: #fff; color: #344b46;
  @media (max-width: 768px) { padding: 16px 12px; }
`;
const Header = styled.header`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
  gap: 12px 20px; margin-bottom: 24px; padding-bottom: 20px;
  border-bottom: 1px solid #e4ebe9;
  @media (max-width: 768px) { padding-bottom: 14px; margin-bottom: 20px; }
`;
const TitleBlock = styled.div`
  flex: 1 1 220px; min-width: 0;
`;
const Title = styled.h1`
  margin: 0; font-size: 22px; font-weight: 750; line-height: 1.4;
  color: #ee754c; overflow-wrap: anywhere;
  svg { width: 20px; height: 20px; vertical-align: middle; }
  @media (max-width: 768px) { font-size: 20px; }
`;
const Subtitle = styled.p`
  margin: 6px 0 0; font-size: 12px; line-height: 1.6;
  color: #768681; overflow-wrap: anywhere;
`;
export const Grid = styled.div`
  display: grid; grid-template-columns: minmax(280px, 380px) minmax(0, 1fr);
  grid-template-areas: "settings preview" "share preview";
  align-items: start; gap: 24px 32px; min-width: 0;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: "settings" "preview" "share";
    gap: 24px;
  }
`;
const Card = styled.section`
  grid-area: settings;
  min-width: 0; padding: 0; background: #fff;
  > :last-child { margin-bottom: 0; }
`;
const ShareCard = styled(Card)`
  grid-area: share;
  border-top: 1px solid #e4ebe9;
  padding-top: 20px;
`;
const PreviewCard = styled(Card)`
  grid-area: preview;
  min-width: 0;
  @media (max-width: 900px) { padding-top: 20px; border-top: 1px solid #e4ebe9; }
`;
const CardTitle = styled.h2`
  display: flex; align-items: center; gap: 8px; margin: 0 0 18px;
  font-size: 14px; line-height: 1.5; font-weight: 750; color: #344b46;
  svg { width: 18px; height: 18px; flex: 0 0 18px; stroke-width: 1.75; color: #6b8c88; }
`;
const Field = styled.div`
  min-width: 0; margin-bottom: 16px;
`;
const Label = styled.label`
  display: block; margin: 0 0 6px; color: #536a64; font-size: 12px; font-weight: 650;
`;
const ReadOnlyText = styled.p`
  margin: 0; padding: 10px 12px; border-radius: 8px; background: #f3f6f5;
  color: #344b46; overflow-wrap: anywhere; font-size: 13px; font-weight: 600;
`;
const Input = styled.input`
  width: 100%; min-width: 0; min-height: 38px; padding: 8px 10px;
  border: 1px solid #dce5e2; border-radius: 8px; background: #fff;
  color: #344b46; font-size: 13px; outline: none;
  &:focus { border-color: #ee754c; box-shadow: 0 0 0 3px #fff1eb; }
`;
const ColorRow = styled.div`
  display: grid; grid-template-columns: 38px minmax(0, 1fr); gap: 8px;
`;
const ColorInput = styled(Input)`
  height: 38px; padding: 3px; cursor: pointer;
  &::-webkit-color-swatch-wrapper { padding: 0; }
  &::-webkit-color-swatch { border: 0; border-radius: 4px; }
`;
const BackgroundChoiceRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px;
`;
const BackgroundChoiceButton = styled(OverviewButton)`
  border-color: ${({ $active }) => $active ? "#ee754c" : "#dce5e2"};
  background: ${({ $active }) => $active ? "#fff3ed" : "#fff"};
  color: ${({ $active }) => $active ? "#c7502b" : "#536a64"};
  &::before {
    content: ""; width: 14px; height: 14px; flex: 0 0 14px; border-radius: 50%;
    border: 1px solid #dce5e2; background: ${({ $dark }) => $dark ? "#2f3437" : "#fff"};
  }
`;
const CodeBox = styled.pre`
  margin: 0 0 10px; padding: 12px; white-space: pre-wrap;
  overflow-wrap: anywhere; word-break: break-word; font-size: 11px; line-height: 1.65;
  color: #536a64; background: #f3f6f5; border: 1px solid #e4ebe9; border-radius: 8px;
`;
const ButtonRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px;
`;
const PreviewSizeRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px;
`;
const PreviewSizeButton = styled(OverviewButton)`
  border-color: ${({ $active }) => $active ? "#ee754c" : "#dce5e2"};
  background: ${({ $active }) => $active ? "#fff3ed" : "#fff"};
  color: ${({ $active }) => $active ? "#c7502b" : "#536a64"};
`;
const PreviewOuter = styled.div`
  display: flex; justify-content: center; align-items: flex-start;
  min-width: 0; padding: 20px; background: #f3f6f5; border-radius: 12px;
  @media (max-width: 600px) { padding: 8px; }
`;
export const PreviewFrameWrap = styled.div`
  width: ${({ $width }) => $width}px; max-width: 100%; min-width: 0;
  height: ${({ $height }) => $height}px; flex-shrink: 1;
  border: 1px solid #dce5e2; border-radius: ${({ $radius }) => $radius}px;
  overflow: hidden; background: ${({ $backgroundColor }) => $backgroundColor};
  box-shadow: 0 4px 16px rgba(52, 75, 70, 0.04);
`;
const PreviewFrame = styled.iframe`
  display: block; width: 100%; height: 100%; border: 0;
`;
const HelpText = styled.p`
  margin: 8px 0 12px; color: #768681; font-size: 11px; line-height: 1.6;
`;
const isHexColor = value => /^#[0-9A-Fa-f]{6}$/.test(value || "");
function RestaurantWidgetSettings() {
  const {
    t
  } = useTranslation();
  const {
    restaurant_id
  } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const restaurantDetails = useSelector(state => state.restaurantDetails);
  const {
    restaurants,
    isLoading,
    error
  } = restaurantDetails;
  const [mainColor, setMainColor] = useState("#EE754C");
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
  const [borderRadius, setBorderRadius] = useState(12);
  const [previewSize, setPreviewSize] = useState("medium");
  useEffect(() => {
    if (restaurant_id) {
      dispatch(fetchRestaurantDetails(restaurant_id));
    }
  }, [dispatch, restaurant_id]);
  const selectedPreview = PREVIEW_SIZES[previewSize];
  const radius = Math.max(0, Math.min(40, Number(borderRadius) || 0));
  const autoWidgetTitle = useMemo(() => {
    const restaurantName = restaurants?.restaurant_name?.trim();
    if (!restaurantName) {
      return t("ui.i18nFinalCleanup.bookTable");
    }
    return `Reservar no ${restaurantName}`;
  }, [restaurants, t]);
  const widgetUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("title", autoWidgetTitle);
    params.set("showName", "0");
    params.set("main", mainColor || "#EE754C");
    params.set("bg", backgroundColor || "#FFFFFF");
    params.set("radius", String(radius));
    return `${window.location.origin}/#/widget/reservations/${restaurant_id}?${params.toString()}`;
  }, [restaurant_id, autoWidgetTitle, mainColor, backgroundColor, radius]);
  const iframeCode = useMemo(() => {
    return `<iframe
  src="${widgetUrl}"
  style="width:100%; height:100%; border:0; border-radius:${radius}px; display:block;"
  loading="lazy"
></iframe>`;
  }, [widgetUrl, radius]);
  const copySuccess = message => {
    toast(message, {
      autoClose: 1200
    });
  };
  return <>
      <ToastContainer />
      <Page>

      <Header>
        <TitleBlock>
          <Title>
            <LuCode2 style={{
            marginRight: "8px"
          }} />{t("ui.screens.restaurantWidgetSettings.widgetDeReservas")}</Title>
          <Subtitle>
            {restaurants?.restaurant_name ? `Configura e copia o widget de ${restaurants.restaurant_name}.` : t("ui.i18nCleanup2.configuraECopiaOWidgetParaOSiteDoRestaurante")}
          </Subtitle>
        </TitleBlock>

        <OverviewButton type="button" onClick={() => navigate(-1)}>
          <LuArrowLeft style={{
          marginRight: "6px"
        }} />{t("ui.screens.restaurantWidgetSettings.voltar")}</OverviewButton>
      </Header>

      {isLoading ? <Loader /> : error ? <Messages variant="danger">{error}</Messages> : <Grid>
          <Card>
            <CardTitle>
              <LuPalette />{t("ui.screens.restaurantWidgetSettings.personalizacao")}</CardTitle>

            <Field>
              <Label>{t("ui.screens.restaurantWidgetSettings.tituloAutomatico")}</Label>
              <ReadOnlyText>{autoWidgetTitle}</ReadOnlyText>
            </Field>

            <Field>
              <Label htmlFor="widget-color">{t("ui.screens.restaurantWidgetSettings.corPrincipal")}</Label>
              <ColorRow>
                <ColorInput aria-label={t("ui.screens.restaurantWidgetSettings.corPrincipal")} type="color" value={isHexColor(mainColor) ? mainColor : "#EE754C"} onChange={event => setMainColor(event.target.value)} />
                <Input id="widget-color" type="text" value={mainColor} onChange={event => setMainColor(event.target.value)} placeholder={t("ui.screens.restaurantWidgetSettings.ee754cOuRgb23811776")} />
              </ColorRow>
            </Field>

            <Field>
              <Label>{t("ui.screens.restaurantWidgetSettings.fundo")}</Label>
              <BackgroundChoiceRow>
                <BackgroundChoiceButton type="button" $active={backgroundColor === "#FFFFFF"} aria-pressed={backgroundColor === "#FFFFFF"} $mainColor={mainColor} $bg="#FFFFFF" onClick={() => setBackgroundColor("#FFFFFF")}>{t("ui.screens.restaurantWidgetSettings.claro")}</BackgroundChoiceButton>

                <BackgroundChoiceButton type="button" $active={backgroundColor === "#2F3437"} aria-pressed={backgroundColor === "#2F3437"} $mainColor={mainColor} $bg="#2F3437" $dark onClick={() => setBackgroundColor("#2F3437")}>{t("ui.screens.restaurantWidgetSettings.escuro")}</BackgroundChoiceButton>
              </BackgroundChoiceRow>
            </Field>

            <Field>
              <Label htmlFor="widget-radius">{t("ui.screens.restaurantWidgetSettings.cantos")}</Label>
              <Input id="widget-radius" type="number" min="0" max="40" onBlur={() => setBorderRadius(radius)} value={borderRadius} onChange={event => setBorderRadius(event.target.value)} />
            </Field>
          </Card>
          <ShareCard>
            <Field>
              <Label>{t("ui.screens.restaurantWidgetSettings.linkDireto")}</Label>
              <CodeBox>{widgetUrl}</CodeBox>

              <ButtonRow>
                <CopyToClipboard text={widgetUrl} onCopy={() => copySuccess(t("ui.i18nFinalCleanup.widgetLinkCopied"))}>
                  <OverviewButton type="button">
                    <LuCopy style={{
                  marginRight: "6px"
                }} />{t("ui.screens.restaurantWidgetSettings.copiarLink")}</OverviewButton>
                </CopyToClipboard>
              </ButtonRow>
            </Field>

            <Field>
              <Label>{t("ui.screens.restaurantWidgetSettings.codigoParaOSite")}</Label>
              <CodeBox>{iframeCode}</CodeBox>
              <HelpText>{t("ui.screens.restaurantWidgetSettings.oIframeUsaAltura100A")}</HelpText>

              <ButtonRow>
                <CopyToClipboard text={iframeCode} onCopy={() => copySuccess(t("ui.i18nFinalCleanup.widgetCodeCopied"))}>
                  <OverviewButton type="button" $primary>
                    <LuCopy style={{
                  marginRight: "6px"
                }} />{t("ui.screens.restaurantWidgetSettings.copiarCodigo")}</OverviewButton>
                </CopyToClipboard>
              </ButtonRow>
            </Field>
          </ShareCard>

          <PreviewCard>
            <CardTitle>
              <LuEye />{t("ui.screens.restaurantWidgetSettings.preVisualizacao")}</CardTitle>

            <PreviewSizeRow>
              {Object.entries(PREVIEW_SIZES).map(([key, size]) => <PreviewSizeButton key={key} type="button" $active={previewSize === key} aria-pressed={previewSize === key} $mainColor={mainColor} onClick={() => setPreviewSize(key)}>
                  {size.label}
                </PreviewSizeButton>)}
            </PreviewSizeRow>

            <PreviewOuter>
              <PreviewFrameWrap $width={selectedPreview.width} $height={selectedPreview.height} $radius={radius} $backgroundColor={backgroundColor}>
                <PreviewFrame key={widgetUrl} title={t("ui.screens.restaurantWidgetSettings.preVisualizacaoDoWidgetDeReservas")} src={widgetUrl} />
              </PreviewFrameWrap>
            </PreviewOuter>
          </PreviewCard>
        </Grid>}
    </Page></>;
}
export default RestaurantWidgetSettings;
